import { useCallback, useEffect, useRef } from 'react';
import type { LlmChatSuggestion } from './llm-chat.util';
import { useLlmSuggestionResolver } from './llm-suggestion-resolver.hook';

/**
 * Versioned accept/reject telemetry for the chat Suggestion artifact: a new
 * version supersedes (rejects) the prior exchange's version, and the consumer
 * resolves the latest version on accept/discard. Built on the shared
 * `useLlmSuggestionResolver` core (dedup + mutation + Sentry).
 * See docs/iterations/AZ-323/AZ-323.DECISIONS.md (D12).
 */
export const useSuggestionResolutionLog = (latestSuggestion: LlmChatSuggestion | undefined) => {
  const { resetResolved, resolveLog } = useLlmSuggestionResolver();

  const lastSuggestionRef = useRef<LlmChatSuggestion | undefined>(undefined);

  useEffect(() => {
    const previous = lastSuggestionRef.current;

    if (
      previous?.logId &&
      latestSuggestion &&
      previous.versionId !== latestSuggestion.versionId &&
      previous.logId !== latestSuggestion.logId
    ) {
      resolveLog(previous.logId, 'reject');
    }

    if (latestSuggestion) {
      lastSuggestionRef.current = latestSuggestion;
    }
  }, [latestSuggestion, resolveLog]);

  const reset = useCallback(() => {
    lastSuggestionRef.current = undefined;
    resetResolved();
  }, [resetResolved]);

  return { reset, resolveLog };
};
